<script setup lang="ts">
import { ref } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import { importLabelingImages } from '@/api/RobVisionApi';
import type { LabelingImportResult } from '@/models/RobVision';
import { useAlertsStore } from '@/store/AlertsStore';
import gettext from '@/i18n/gettext';

const { $gettext, $ngettext } = gettext;
const alertsStore = useAlertsStore();

const props = defineProps<{
  projectUuid: string;
}>();

const isOpen = defineModel<boolean>('isOpen', { required: true });

const emit = defineEmits<{
  imported: [];
}>();

const file = ref<File | null>(null);
const uploading = ref(false);
const errorMessage = ref('');
const result = ref<LabelingImportResult | null>(null);

function reset() {
  file.value = null;
  errorMessage.value = '';
  result.value = null;
}

function closeDialog() {
  isOpen.value = false;
  reset();
}

async function upload() {
  if (!file.value) {
    return;
  }
  uploading.value = true;
  errorMessage.value = '';
  try {
    result.value = await importLabelingImages(props.projectUuid, file.value);
    if (result.value.imported > 0) {
      emit('imported');
      // Successful import: close right away, confirm via snackbar. The
      // dialog stays open only when nothing was imported, so the
      // duplicate/skip details remain visible.
      let message = importedText(result.value.imported);
      if (result.value.duplicateFolders.length > 0) {
        message +=
          ' ' +
          $ngettext(
            '%{ n } was already imported earlier.',
            '%{ n } were already imported earlier.',
            result.value.duplicateFolders.length,
            { n: String(result.value.duplicateFolders.length) },
          );
      }
      alertsStore.success(message);
      closeDialog();
    }
    file.value = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    errorMessage.value = error.message;
  } finally {
    uploading.value = false;
  }
}

function importedText(imported: number): string {
  return $ngettext(
    '%{ n } image imported.',
    '%{ n } images imported.',
    imported,
    { n: String(imported) },
  );
}
</script>

<template>
  <v-dialog v-model="isOpen" max-width="560" @after-leave="reset">
    <v-card rounded="lg">
      <v-card-title>{{ $gettext('Import Images') }}</v-card-title>
      <v-card-subtitle class="text-wrap">
        {{
          $gettext(
            'Upload a RobFlow results export (.zip). Each captured frame becomes an image to label; frames with edge detections arrive prelabeled.',
          )
        }}
      </v-card-subtitle>
      <v-card-text>
        <v-file-input
          v-model="file"
          accept=".zip"
          :label="$gettext('RobFlow export (.zip)')"
          prepend-icon="mdi-folder-zip"
          density="comfortable"
          :disabled="uploading"
        />
        <v-alert
          v-if="errorMessage"
          type="error"
          density="compact"
          variant="tonal"
          class="mb-2"
        >
          {{ errorMessage }}
        </v-alert>
        <v-alert
          v-if="result"
          :type="result.imported > 0 ? 'success' : 'warning'"
          density="compact"
          variant="tonal"
        >
          <div>{{ importedText(result.imported) }}</div>
          <div v-if="result.duplicateFolders.length > 0" class="text-caption">
            {{
              $gettext('Already imported earlier: %{ folders }', {
                folders: result.duplicateFolders.join(', '),
              })
            }}
          </div>
          <div v-if="result.skippedFolders.length > 0" class="text-caption">
            {{
              $gettext('Skipped (no usable capture): %{ folders }', {
                folders: result.skippedFolders.join(', '),
              })
            }}
          </div>
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <r-btn variant="outlined" @click="closeDialog">
          {{ result ? $gettext('Close') : $gettext('Cancel') }}
        </r-btn>
        <v-spacer />
        <r-btn
          variant="accent"
          icon="mdi-upload"
          :disabled="!file || uploading"
          :loading="uploading"
          @click="upload"
        >
          {{ $gettext('Import') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
